import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import {
  symbolData,
  isCoinSymbol,
  isJackpotSymbol,
  isBonusSymbol,
  ZEUS_COLLECT_ID,
  ZEUS_DOUBLE_ID,
  ZEUS_MYSTERY_ID,
} from "../../SymbolData";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";
import { Settings } from "../../settings";
import store from "../../State/Store/store";
import { formatToCustomLocale } from "../../utilities";
import { FONT_STYLES, scaleStyle } from "../../fontStyles";



/**
 * PotCollectionOverlaySymbolController
 *
 * Manages spine animations for a single symbol position in the Pot Collection bonus.
 * Pre-creates spines for all PC-relevant symbol types (coins c, jackpots j, zeus orbs cs)
 * so any can be shown instantly via showIdleAnimation(symbolId).
 *
 * Key differences from RoyalCollectOverlaySymbolController:
 * - Uses GrekoNew PIXI.Text (not BitmapFont) for coin values, centered
 * - Adds Zeus orb animations: activate, shuffle, transform (cs spine)
 * - No royal symbol concept
 * - Text slot names: txt_slot_coin_value (c), txt_slot_coin_value_double (j),
 *   txt_slot_cm_value / txt_slot_cd_value / txt_slot_cc_value (cs)
 */
class PotCollectionOverlaySymbolController {
  constructor(
    overlaySymbolContainer,
    reelIndex,
    symbolIndex,
    symbolWidth,
    symbolHeight,
  ) {
    this.overlaySymbolContainer = overlaySymbolContainer;
    this.reelIndex = reelIndex;
    this.symbolIndex = symbolIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this.prePositionedSpines = new Map();
    this.activeSpineKey = null;
    this.isAnimating = false;
    this.currentAnimationType = null;
    this.currentSymbolId = null;

    this.animationCaches = new Map();
    this.animationAvailability = new Map();

    // Coin value text (PIXI.Text, not BitmapText)
    this.coinValueContainer = null;
    this.coinValueText = null;
    this._coinValueSpine = null;

    this._spineScaleCache = new Map();
    this._coinDisplayValueCache = new Map();

    this.initializeAllSpines();
  }

  // ── Spine Initialization ────────────────────────────────────────────

  initializeAllSpines() {
    if (!this.overlaySymbolContainer) return;

    // PC-relevant spines: c (coins), j (jackpots), cs (zeus orbs)
    const spineNames = new Set();
    const pcSymbolIds = [15, 16, 17, 18, 19, 12, 13, 14]; // c1, bl, jackpots, orbs
    for (const id of pcSymbolIds) {
      const info = symbolData[id];
      if (info?.spine && info.spine !== "bl") {
        spineNames.add(info.spine);
      }
    }

    for (const spineName of spineNames) {
      const spine = this._createSpineInstance(spineName);
      if (!spine) continue;

      const scaleData = this._calculateSpineScale(
        this.symbolWidth,
        this.symbolHeight,
        spineName,
      );

      spine.position.set(scaleData.x, scaleData.y);
      spine.scale.set(scaleData.scale);
      spine.zIndex = 10;
      spine.alpha = 0.001;
      spine.autoUpdate = false;

      // Fix bounds so addSlotObject text can't expand the spine's PIXI bounds.
      // Without this, wider text in the slot causes the spine's bounds to grow,
      // which can cause PIXI to resize the spine when parent containers
      // sort/layout children.
      spine.boundsArea = new PIXI.Rectangle(
        -this.symbolWidth / 2,
        -this.symbolHeight / 2,
        this.symbolWidth,
        this.symbolHeight,
      );

      this.overlaySymbolContainer.addChild(spine);
      this.prePositionedSpines.set(spineName, spine);

      if (spine.skeleton?.data?.animations?.length > 0) {
        const available = new Set(
          spine.skeleton.data.animations.map((a) => a.name.toLowerCase()),
        );
        this.animationAvailability.set(spineName, available);
      }

      // Warm up on first position only
      if (this.reelIndex === 0 && this.symbolIndex === 0) {
        this._warmUpAnimation(spine, spineName, "idle");
        this._warmUpAnimation(spine, spineName, "in");
        this._warmUpAnimation(spine, spineName, "win");
      }
    }
  }

  _createSpineInstance(spineName) {
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });
      spine.name = `pc_spine_${spineName}_${this.reelIndex}_${this.symbolIndex}`;
      return spine;
    } catch {
      return null;
    }
  }

  _calculateSpineScale(containerWidth, containerHeight, spineName) {
    const cacheKey = `${containerWidth}_${containerHeight}_${spineName || ''}`;
    if (this._spineScaleCache.has(cacheKey)) {
      return this._spineScaleCache.get(cacheKey);
    }

    const nativeHeight = Settings.SYMBOL_HEIGHT;
    // No padding in Pot Collection — all symbols render at natural size
    const padding = 0;
    const scale = (containerHeight / nativeHeight) * (1 - padding * 2);

    const result = { scale, x: 0, y: 0 };
    this._spineScaleCache.set(cacheKey, result);
    return result;
  }

  // ── Animation Selection ─────────────────────────────────────────────

  /**
   * Select animation by type. For cs spine, zeus orb animations use
   * prefixed names like "CM_Red/activate_red_cm".
   */
  _selectAnimation(spineName, type) {
    let cache = this.animationCaches.get(spineName);
    if (!cache) {
      cache = new Map();
      this.animationCaches.set(spineName, cache);
    }

    if (cache.has(type)) return cache.get(type);

    const spine = this.prePositionedSpines.get(spineName);
    if (!spine?.skeleton?.data?.animations) return null;

    const lowerType = type.toLowerCase();

    // Landing animation aliases — animators use "in", "land", or "landing"
    // interchangeably. Try all as exact matches to avoid partial match
    // collisions (e.g. "in" matching "win" or "darken_...").
    const LANDING_ALIASES = ["in", "land", "landing"];
    const isLandingType = LANDING_ALIASES.includes(lowerType);
    const exactNames = isLandingType ? LANDING_ALIASES : [lowerType];

    // Exact match (try all aliases for landing types)
    for (const name of exactNames) {
      for (const anim of spine.skeleton.data.animations) {
        if (anim.name.toLowerCase() === name) {
          cache.set(type, anim.name);
          return anim.name;
        }
      }
    }

    // Partial match (skip for landing types — too ambiguous)
    if (!isLandingType) {
      for (const anim of spine.skeleton.data.animations) {
        if (anim.name.toLowerCase().includes(lowerType)) {
          cache.set(type, anim.name);
          return anim.name;
        }
      }
    }

    // Fallback: "idle" → "static"
    if (type === "idle") {
      for (const anim of spine.skeleton.data.animations) {
        if (anim.name === "static") {
          cache.set(type, anim.name);
          return anim.name;
        }
      }
    }

    return null;
  }

  _hasAnimation(spineName, type) {
    const available = this.animationAvailability.get(spineName);
    if (!available) return false;
    const lowerType = type.toLowerCase();

    // Landing aliases: check for exact matches of "in", "land", "landing"
    const LANDING_ALIASES = ["in", "land", "landing"];
    if (LANDING_ALIASES.includes(lowerType)) {
      for (const alias of LANDING_ALIASES) {
        for (const name of available) {
          if (name.toLowerCase() === alias) return true;
        }
      }
      return false;
    }

    for (const name of available) {
      if (name.includes(lowerType)) return true;
    }
    if (type === "idle" && available.has("static")) return true;
    return false;
  }

  _warmUpAnimation(spine, spineName, type) {
    const animName = this._selectAnimation(spineName, type);
    if (!animName || !spine?.state) return;

    try {
      spine.state.clearTracks();
      spine.state.addAnimation(0, animName, true, 0);
      spine.state.update(0.016);
      spine.alpha = 0.001;

      requestAnimationFrame(() => {
        if (this.isAnimating) return;
        if (spine?.state) spine.state.clearTracks();
        if (spine) spine.alpha = 0.001;
      });
    } catch {
      // Silently ignore warmup errors
    }
  }

  // ── Get Spine for Symbol ────────────────────────────────────────────

  _getSpineForSymbol(symbolId) {
    const info = symbolData[symbolId];
    if (!info?.spine) return null;

    const spine = this.prePositionedSpines.get(info.spine);
    if (!spine) return null;

    // Apply skin if defined
    if (info.skin && spine.skeleton?.data) {
      const skin = spine.skeleton.data.findSkin(info.skin);
      if (skin) {
        spine.skeleton.setSkin(skin);
        spine.skeleton.setSlotsToSetupPose();
      }
    } else if (info.name && spine.skeleton?.data) {
      const skin = spine.skeleton.data.findSkin(info.name);
      if (skin) {
        spine.skeleton.setSkin(skin);
        spine.skeleton.setSlotsToSetupPose();
      }
    }

    return spine;
  }

  // ── Coin Display Value ──────────────────────────────────────────────

  _getCoinDisplayValue(symbolId) {
    // Read dynamic value from API response (initialRespinValues[reel][row])
    // Falls back to static symbolData[symbolId].value if not available.
    // Values are pre-calculated subunit amounts (real server) or normalized
    // to subunits by the RGS adapter (sim) — no bet multiplication needed.
    let symbolValue = null;

    try {
      const state = store.getState();
      const values = state.potCollection?.valuesGrid
        || state.potCollection?.initialRespinValues;

      if (values && values[this.reelIndex]) {
        const dynamicValue = values[this.reelIndex][this.symbolIndex];
        if (dynamicValue && dynamicValue > 0) {
          symbolValue = dynamicValue;

          // BE sends post-feature values. Coins must spawn with pre-double values
          // so the double animation visually changes them.
          // Count d_doubles in screenProgress that include this position and divide.
          const screenProgress = state.potCollection?.screenProgress;
          if (screenProgress) {
            let doublesCount = 0;
            for (const action of screenProgress) {
              if (action.action !== 'd_doubles') continue;
              const coords = action.coordinates;
              if (!coords) continue;
              // coords is array-of-pairs: [[r,c], [r,c], ...]
              for (const coord of coords) {
                if (Array.isArray(coord) && coord[0] === this.reelIndex && coord[1] === this.symbolIndex) {
                  doublesCount++;
                  break;
                }
              }
            }
            if (doublesCount > 0) {
              symbolValue = Math.round(symbolValue / Math.pow(2, doublesCount));
            }
          }
        }
      }

      // Fallback to static symbolData if no dynamic value
      if (!symbolValue) {
        symbolValue = symbolData[symbolId]?.value;
      }
      if (!symbolValue) return "";

      const cacheKey = `${symbolId}_${symbolValue}`;
      if (this._coinDisplayValueCache.has(cacheKey)) {
        return this._coinDisplayValueCache.get(cacheKey);
      }

      const coinValue = parseInt(symbolValue, 10);
      if (isNaN(coinValue) || coinValue <= 0) return "";

      // Values are already in subunits — just format, no bet multiplication
      const formatted = formatToCustomLocale(coinValue);
      this._coinDisplayValueCache.set(cacheKey, formatted);

      return formatted;
    } catch {
      return "";
    }
  }

  // ── Coin Value Text (PIXI.Text with GrekoNew) ──────────────────────

  /**
   * Determine the correct txt_slot name for a given symbolId.
   */
  _getTextSlotName(symbolId) {
    if (isCoinSymbol(symbolId)) return "txt_slot_coin_value";
    if (isJackpotSymbol(symbolId)) return "txt_slot_coin_value_double";
    // Zeus orbs (cs spine)
    const info = symbolData[symbolId];
    if (info?.name === "cm") return "txt_slot_cm_value";
    if (info?.name === "cd") return "txt_slot_cd_value";
    if (info?.name === "cc") return "txt_slot_cc_value";
    return null;
  }

  createCoinValueText(displayValue) {
    if (!displayValue) return null;

    const counterContainer = new PIXI.Container();
    counterContainer.name = "pc_coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.zIndex = 1000;

    const fontSize =
      this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    const baseStyle = FONT_STYLES.coinValue;
    const textStyle = new PIXI.TextStyle(scaleStyle(baseStyle, fontSize));

    const text = new PIXI.Text(displayValue, textStyle);
    text.anchor.set(0.5);
    text.roundPixels = true;

    // Progressive width compression (matches HnW pattern):
    // ≤8 digits: squish X only (preserve height)
    // 9+ digits: uniform scale
    const maxTextWidth = this.symbolWidth * (1 - 0.05 * 2);
    if (text.width > maxTextWidth) {
      const digitCount = displayValue.replace(/[^0-9]/g, '').length;
      const scaleRatio = maxTextWidth / text.width;
      if (digitCount <= 8) {
        text.scale.x = scaleRatio;
      } else {
        text.scale.set(scaleRatio);
      }
    }

    counterContainer.addChild(text);
    return { counterContainer, text };
  }

  autoFitCoinValueText() {
    if (!this.coinValueText || this.coinValueText.destroyed) return;

    this.coinValueText.scale.set(1);

    let totalScale = 1;
    if (this._coinValueSpine) {
      const spineScale = this._coinValueSpine.scale.x || 1;
      const slotName = this._getTextSlotName(this.currentSymbolId);
      const slot = slotName ? this._coinValueSpine.skeleton?.findSlot(slotName) : null;
      const slotBone = slot?.bone;
      const boneBaseScale = Math.abs(slotBone?.a) || 1;
      totalScale = spineScale * boneBaseScale;
    }

    // Recalculate fontSize to match current symbolHeight (survives resize during feature execution)
    const baseFontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const scaledFontSize = totalScale !== 0 ? baseFontSize / totalScale : baseFontSize;
    if (Math.abs(this.coinValueText.style.fontSize - scaledFontSize) > 0.5) {
      const baseStyle = FONT_STYLES.coinValue;
      Object.assign(this.coinValueText.style, scaleStyle(baseStyle, scaledFontSize));
    }

    const paddingPercent = 0.05;
    const maxTextWidth = totalScale !== 0
      ? (this.symbolWidth * (1 - paddingPercent * 2)) / totalScale
      : this.symbolWidth * (1 - paddingPercent * 2);
    const actualTextWidth = this.coinValueText.width;

    if (actualTextWidth > maxTextWidth) {
      const displayValue = this.coinValueText.text || "";
      const digitCount = displayValue.replace(/[^0-9]/g, '').length;
      const scaleRatio = maxTextWidth / actualTextWidth;
      if (digitCount <= 8) {
        this.coinValueText.scale.x = scaleRatio;
      } else {
        this.coinValueText.scale.set(scaleRatio);
      }
    }
  }

  showCoinValueText(symbolId, spineInstance) {
    this.hideCoinValueText();

    const displayValue = this._getCoinDisplayValue(symbolId);
    if (!displayValue) return;

    const result = this.createCoinValueText(displayValue);
    if (!result) return;

    this.coinValueContainer = result.counterContainer;
    this.coinValueText = result.text;
    this._coinValueSpine = spineInstance;

    const slotName = this._getTextSlotName(symbolId);
    if (!slotName) return;

    if (spineInstance) {
      const spineScale = spineInstance.scale.x || 1;
      spineInstance.update(0);

      // Find the bone via the actual slot (not a hardcoded "value" bone name)
      const slot = spineInstance.skeleton?.findSlot(slotName);
      const slotBone = slot?.bone;
      const boneBaseScale = Math.abs(slotBone?.a) || 1;

      const totalScale = spineScale * boneBaseScale;

      if (totalScale && totalScale !== 0) {
        const baseFontSize =
          this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const scaledFontSize = baseFontSize / totalScale;

        const baseStyle = FONT_STYLES.coinValue;
        Object.assign(this.coinValueText.style, scaleStyle(baseStyle, scaledFontSize));
        this.coinValueText.scale.set(1);

        const maxTextWidth = (this.symbolWidth * (1 - 0.05 * 2)) / totalScale;


        if (this.coinValueText.width > maxTextWidth) {
          const displayValue = this.coinValueText.text || "";
          const digitCount = displayValue.replace(/[^0-9]/g, '').length;
          const scaleRatio = maxTextWidth / this.coinValueText.width;
          if (digitCount <= 8) {
            this.coinValueText.scale.x = scaleRatio;
          } else {
            this.coinValueText.scale.set(scaleRatio);
          }

        }
      }

      spineInstance.addSlotObject(slotName, this.coinValueContainer);
    }
  }

  hideCoinValueText() {
    if (this._coinValueSpine && this.coinValueContainer && !this.coinValueContainer.destroyed) {
      const slotName = this._getTextSlotName(this.currentSymbolId);
      if (slotName) {
        try {
          this._coinValueSpine.removeSlotObject(slotName, this.coinValueContainer);
        } catch {
          // Silent
        }
      }
    }

    if (this.coinValueContainer) {
      if (this.coinValueContainer.parent) {
        this.coinValueContainer.parent.removeChild(this.coinValueContainer);
      }
      this.coinValueContainer.destroy({ children: true });
      this.coinValueContainer = null;
      this.coinValueText = null;
    }
    this._coinValueSpine = null;
  }

  // ── Hide Active Spine ───────────────────────────────────────────────

  _hideActiveSpine() {
    if (!this.activeSpineKey) return;

    // Clean up text BEFORE clearing currentSymbolId — hideCoinValueText
    // needs it to find the slot name for removeSlotObject()
    this.hideCoinValueText();

    const spine = this.prePositionedSpines.get(this.activeSpineKey);
    if (spine) {
      spine.alpha = 0.001;
      spine.autoUpdate = false;
      if (spine.state) spine.state.clearTracks();
      if (spine.skeleton?.setToSetupPose) spine.skeleton.setToSetupPose();
    }

    this.activeSpineKey = null;
    this.isAnimating = false;
    this.currentAnimationType = null;
    this.currentSymbolId = null;
  }

  // ── Public Animation Methods ────────────────────────────────────────

  showIdleAnimation(symbolId) {
    if (!this.overlaySymbolContainer) return;
    // Allow coins, jackpots, and zeus orbs
    if (!isCoinSymbol(symbolId) && !isJackpotSymbol(symbolId) && !isBonusSymbol(symbolId)) return;

    if (this.activeSpineKey) this._hideActiveSpine();

    const spine = this._getSpineForSymbol(symbolId);
    if (!spine) return;

    const info = symbolData[symbolId];
    const spineName = info.spine;

    this.isAnimating = true;
    this.activeSpineKey = spineName;
    this.currentAnimationType = "idle";
    this.currentSymbolId = symbolId;

    const idleAnim = this._selectAnimation(spineName, "idle");
    if (spine.state && idleAnim) {
      spine.state.clearTracks();
      spine.state.setAnimation(0, idleAnim, true);
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame so skeleton pose matches before any pixel renders.
    spine.state?.update(0);
    spine.state?.apply(spine.skeleton);
    spine.alpha = 1;
    spine.autoUpdate = true;

    if (info.value && !isJackpotSymbol(symbolId)) {
      this.showCoinValueText(symbolId, spine);
    }
  }

  /**
   * Show coin value text using post-feature values directly.
   * Used on mid-bonus reload where all features (doubles, collects, mystery)
   * have already been applied. Reads valuesGrid raw — no doubling division.
   *
   * Works on the ACTIVE spine (cs spine after playNamedAnimation).
   */
  showReloadCoinValue(symbolId) {
    const spine = this.getActiveSpine();
    if (!spine) return;

    this.hideCoinValueText();

    // Read post-feature value directly from valuesGrid
    try {
      const state = store.getState();
      const values = state.potCollection?.valuesGrid
        || state.potCollection?.initialRespinValues;
      const cellValue = values?.[this.reelIndex]?.[this.symbolIndex];
      if (!cellValue || cellValue <= 0) return;

      const formatted = formatToCustomLocale(cellValue);
      if (!formatted) return;

      const result = this.createCoinValueText(formatted);
      if (!result) return;

      this.coinValueContainer = result.counterContainer;
      this.coinValueText = result.text;
      this._coinValueSpine = spine;

      const slotName = this._getTextSlotName(symbolId);
      if (!slotName) return;

      const spineScale = spine.scale.x || 1;
      spine.update(0);
      const slot = spine.skeleton?.findSlot(slotName);
      const slotBone = slot?.bone;
      const boneBaseScale = Math.abs(slotBone?.a) || 1;
      const totalScale = spineScale * boneBaseScale;

      if (totalScale && totalScale !== 0) {
        const baseFontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const scaledFontSize = baseFontSize / totalScale;
        const baseStyle = FONT_STYLES.coinValue;
        Object.assign(this.coinValueText.style, scaleStyle(baseStyle, scaledFontSize));
        this.coinValueText.scale.set(1);

        const maxTextWidth = (this.symbolWidth * (1 - 0.05 * 2)) / totalScale;
        if (this.coinValueText.width > maxTextWidth) {
          const displayValue = this.coinValueText.text || "";
          const digitCount = displayValue.replace(/[^0-9]/g, '').length;
          const scaleRatio = maxTextWidth / this.coinValueText.width;
          if (digitCount <= 8) {
            this.coinValueText.scale.x = scaleRatio;
          } else {
            this.coinValueText.scale.set(scaleRatio);
          }
        }
      }

      spine.addSlotObject(slotName, this.coinValueContainer);
    } catch {
      // Fail silently
    }
  }

  showLandingAnimation(symbolId) {
    if (!this.overlaySymbolContainer) return;
    if (!isCoinSymbol(symbolId) && !isJackpotSymbol(symbolId) && !isBonusSymbol(symbolId)) return;

    // Play landing sound based on symbol type
    if (isCoinSymbol(symbolId)) {
      soundManager.play(SOUND_NAME.c_in);
    } else if (symbolId === ZEUS_COLLECT_ID) {
      soundManager.play(SOUND_NAME.cc_in);
    } else if (symbolId === ZEUS_DOUBLE_ID) {
      soundManager.play(SOUND_NAME.cd_in);
    } else if (symbolId === ZEUS_MYSTERY_ID) {
      soundManager.play(SOUND_NAME.cm_in);
    }

    if (this.activeSpineKey) this._hideActiveSpine();

    const spine = this._getSpineForSymbol(symbolId);
    if (!spine) return;

    const info = symbolData[symbolId];
    const spineName = info.spine;

    this.isAnimating = true;
    this.activeSpineKey = spineName;
    this.currentAnimationType = "landing";
    this.currentSymbolId = symbolId;

    // "in" animation = coin appear/landing
    const inAnim = this._selectAnimation(spineName, "in");
    const idleAnim = this._selectAnimation(spineName, "idle");

    if (spine.state) {
      spine.state.clearTracks();
      const winAnim = isCoinSymbol(symbolId) ? this._selectAnimation(spineName, "win") : null;
      if (inAnim && winAnim && idleAnim) {
        // Regular coins: land → win → idle
        spine.state.setAnimation(0, inAnim, false);
        spine.state.addAnimation(0, winAnim, false, 0);
        spine.state.addAnimation(0, idleAnim, true, 0);
        // Play c_win when win animation starts
        spine.state.addListener({
          start: (entry) => {
            if (entry.animation.name === winAnim) {
              soundManager.play(SOUND_NAME.c_win);
              spine.state.removeListener(this);
            }
          },
        });
      } else if (inAnim && idleAnim) {
        spine.state.setAnimation(0, inAnim, false);
        spine.state.addAnimation(0, idleAnim, true, 0);
      } else if (idleAnim) {
        spine.state.setAnimation(0, idleAnim, true);
      } else if (inAnim) {
        spine.state.setAnimation(0, inAnim, false);
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    spine.state?.update(0);
    spine.state?.apply(spine.skeleton);
    spine.alpha = 1;
    spine.autoUpdate = true;

    if (info.value && !isJackpotSymbol(symbolId)) {
      this.showCoinValueText(symbolId, spine);
    }
  }

  showWinAnimation(symbolId, loop = true, showText = true) {
    if (!this.overlaySymbolContainer) return;
    if (!isCoinSymbol(symbolId) && !isJackpotSymbol(symbolId) && !isBonusSymbol(symbolId)) return;

    if (this.activeSpineKey) this._hideActiveSpine();

    const spine = this._getSpineForSymbol(symbolId);
    if (!spine) return;

    const info = symbolData[symbolId];
    const spineName = info.spine;

    this.isAnimating = true;
    this.activeSpineKey = spineName;
    this.currentAnimationType = "win";
    this.currentSymbolId = symbolId;

    const winAnim = this._selectAnimation(spineName, "win");
    if (spine.state && winAnim) {
      spine.state.clearTracks();
      spine.state.setAnimation(0, winAnim, loop);

      if (!loop) {
        const idleAnim = this._selectAnimation(spineName, "idle");
        if (idleAnim) {
          spine.state.addAnimation(0, idleAnim, true, 0);
        }
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    spine.state?.update(0);
    spine.state?.apply(spine.skeleton);
    spine.alpha = 1;
    spine.autoUpdate = true;

    if (showText && info.value && !isJackpotSymbol(symbolId) && !isBonusSymbol(symbolId)) {
      this.showCoinValueText(symbolId, spine);
    }
  }

  /**
   * Play a named animation directly on the cs spine (for Zeus orb features).
   * Used for activate, shuffle, transform sequences.
   *
   * If the cs spine is already the active spine (e.g. playing idle), we transition
   * in-place with setAnimation — no hide/re-show, no skin reset. This prevents
   * the visual flash and double-rendering that _hideActiveSpine's setToSetupPose
   * would cause.
   *
   * @param {string} animationName - Full animation name e.g. "CM_Red/activate_red_cm"
   * @param {boolean} loop - Whether to loop
   * @param {Function} [onComplete] - Callback when animation completes (non-looping only)
   * @param {number} [symbolId] - Zeus symbol ID (12=cd, 13=cm, 14=cc). When provided,
   *   sets currentSymbolId and pre-creates an empty text container attached to the
   *   correct slot so value text is ready before any feature execution starts.
   */
  playNamedAnimation(animationName, loop = false, onComplete = null, symbolId = null) {
    const csSpine = this.prePositionedSpines.get("cs");
    if (!csSpine?.state) return;

    // Clean up any existing named-animation listener before setting a new one
    if (this._namedListener) {
      csSpine.state.removeListener(this._namedListener);
      this._namedListener = null;
    }

    // If a DIFFERENT spine is active, hide it first
    if (this.activeSpineKey && this.activeSpineKey !== "cs") {
      this._hideActiveSpine();
    }

    // Ensure cs spine is updating
    csSpine.autoUpdate = true;
    this.isAnimating = true;
    this.activeSpineKey = "cs";
    this.currentAnimationType = "named";

    // Record which symbol this spine represents (needed for text slot lookup)
    if (symbolId != null) {
      this.currentSymbolId = symbolId;

      // Apply the correct skin for this symbol (e.g. 'cd' for double, 'cc' for collect)
      const info = symbolData[symbolId];
      const skinName = info?.skin || info?.name;
      if (skinName && csSpine.skeleton?.data) {
        const skin = csSpine.skeleton.data.findSkin(skinName);
        if (skin) {
          csSpine.skeleton.setSkin(skin);
          csSpine.skeleton.setSlotsToSetupPose();
        }
      }

      // Pre-create empty text container and attach to the correct slot.
      // Skip for bonus/zeus symbols (cs spine) — they never show text;
      // text only appears on the j spine after mystery swap.
      if (!isBonusSymbol(symbolId) && (!this.coinValueText || this.coinValueText.destroyed)) {
        const slotName = this._getTextSlotName(symbolId);
        if (slotName) {
          const result = this.createCoinValueText(" ");
          if (result) {
            this.coinValueContainer = result.counterContainer;
            this.coinValueText = result.text;
            this._coinValueSpine = csSpine;

            // Counter spine PIXI scale and bone world scale (same as showCoinValueText)
            const spineScale = csSpine.scale.x || 1;
            csSpine.update(0);
            const csSlot = csSpine.skeleton?.findSlot(slotName);
            const slotBone = csSlot?.bone;
            const boneBaseScale = Math.abs(slotBone?.a) || 1;
            const totalScale = spineScale * boneBaseScale;

            if (totalScale && totalScale !== 0) {
              const baseFontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
              this.coinValueText.style.fontSize = baseFontSize / totalScale;
              this.coinValueText.scale.set(1);
            }

            csSpine.addSlotObject(slotName, this.coinValueContainer);
          }
        }
      }
    }

    // Transition directly — setAnimation replaces the current animation in-place
    // without resetting skin or clearing the setup pose
    const entry = csSpine.state.setAnimation(0, animationName, loop);

    // Force the first keyframe onto the skeleton BEFORE the next render tick.
    // Without this, setSlotsToSetupPose (called during skin switch above) leaves
    // the skin's default attachments visible for one frame — e.g. shuffle letters
    // for Mystery or x2 text for Double flash briefly over the ongoing idle.
    // A tiny positive delta (not 0) is needed because update(0) is a no-op in Spine.
    csSpine.state.update(1 / 120);
    csSpine.state.apply(csSpine.skeleton);
    csSpine.alpha = 1;

    if (!loop && onComplete && entry) {
      const listener = {
        complete: (e) => {
          if (e.animation?.name === animationName) {
            csSpine.state.removeListener(listener);
            this._namedListener = null;
            onComplete();
          }
        },
      };
      csSpine.state.addListener(listener);
      this._namedListener = listener;
    }
  }

  /**
   * Queue an animation after the current one on the cs spine.
   */
  queueNamedAnimation(animationName, loop = false) {
    const csSpine = this.prePositionedSpines.get("cs");
    if (!csSpine?.state) return;
    csSpine.state.addAnimation(0, animationName, loop, 0);
  }

  hideAnimation() {
    this._hideActiveSpine();
  }

  /**
   * Get the active spine instance (for position calculations, trail targets, etc.)
   */
  getActiveSpine() {
    if (!this.activeSpineKey) return null;
    return this.prePositionedSpines.get(this.activeSpineKey) || null;
  }

  // ── Resize ──────────────────────────────────────────────────────────

  resizeAnimations(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this._spineScaleCache.clear();

    for (const [spineName, spine] of this.prePositionedSpines.entries()) {
      const scaleData = this._calculateSpineScale(symbolWidth, symbolHeight, spineName);
      spine.position.set(scaleData.x, scaleData.y);
      spine.scale.set(scaleData.scale);
    }

    if (this.coinValueText && !this.coinValueText.destroyed) {
      let totalScale = 1;
      if (this._coinValueSpine) {
        const spineScale = this._coinValueSpine.scale.x || 1;
        this._coinValueSpine.update(0);
        const slotName = this._getTextSlotName(this.currentSymbolId);
        const slot = slotName ? this._coinValueSpine.skeleton?.findSlot(slotName) : null;
        const slotBone = slot?.bone;
        const boneBaseScale = Math.abs(slotBone?.a) || 1;
        totalScale = spineScale * boneBaseScale;
      }

      const baseFontSize =
        this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      this.coinValueText.style.fontSize = totalScale !== 0 ? baseFontSize / totalScale : baseFontSize;
      this.coinValueText.scale.set(1);

      const maxTextWidth = totalScale !== 0
        ? (this.symbolWidth * (1 - 0.05 * 2)) / totalScale
        : this.symbolWidth * (1 - 0.05 * 2);
      if (this.coinValueText.width > maxTextWidth) {
        const displayValue = this.coinValueText.text || "";
        const digitCount = displayValue.replace(/[^0-9]/g, '').length;
        const scaleRatio = maxTextWidth / this.coinValueText.width;
        if (digitCount <= 8) {
          this.coinValueText.scale.x = scaleRatio;
        } else {
          this.coinValueText.scale.set(scaleRatio);
        }
      }
    }
  }

  // ── Cleanup ─────────────────────────────────────────────────────────

  cleanup() {
    this.hideCoinValueText();

    for (const spine of this.prePositionedSpines.values()) {
      if (spine.state) spine.state.clearTracks();
      if (spine.parent) spine.parent.removeChild(spine);
      spine.destroy();
    }
    this.prePositionedSpines.clear();

    this.animationCaches.clear();
    this.animationAvailability.clear();
    this._spineScaleCache.clear();
    this._coinDisplayValueCache.clear();
  }
}

export default PotCollectionOverlaySymbolController;
